import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { client, ensureConnected } from './client';

type Mode = 'password' | 'session';
type Phase = 'form' | 'submitting' | 'done' | 'error';

function getTokenFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return (params.get('token') || '').trim();
}

async function postConnect(token: string, memberId: number, passHash: string) {
  const res = await fetch('/api/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, memberId, passHash }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    memberId?: number;
    name?: string | null;
  };
  if (!res.ok || !data.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

export default function App() {
  const token = useMemo(() => getTokenFromUrl(), []);
  const [mode, setMode] = useState<Mode>('password');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState('');
  const [doneName, setDoneName] = useState<string | null>(null);
  const [doneMemberId, setDoneMemberId] = useState<number | null>(null);

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [captchaUrl, setCaptchaUrl] = useState('');
  const [captchaHash, setCaptchaHash] = useState<string | number>(0);
  const [captchaText, setCaptchaText] = useState('');

  const [memberId, setMemberId] = useState('');
  const [passHash, setPassHash] = useState('');

  if (!token) {
    return (
      <div className="card">
        <h1>Нет токена</h1>
        <p className="lead">
          Откройте одноразовую ссылку из Telegram-бота (команда <code>/connect</code>).
          Прямой заход на сайт без токена не подключает сессию.
        </p>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="card">
        <h1 className="ok">Сессия подключена</h1>
        <p className="lead">
          member_id {doneMemberId}
          {doneName ? ` · ${doneName}` : ''}. Можно закрыть вкладку — бот уже использует сессию.
        </p>
      </div>
    );
  }

  const busy = phase === 'submitting';

  async function finishWithSession(id: number, hash: string) {
    setPhase('submitting');
    setError('');
    try {
      const result = await postConnect(token, id, hash);
      setDoneMemberId(result.memberId ?? id);
      setDoneName(result.name ?? null);
      setPhase('done');
      try {
        client.auth.logout();
      } catch {
        // ignore
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('form');
    }
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!login.trim() || !password) {
      setError('Введите логин и пароль');
      return;
    }
    setPhase('submitting');
    setError('');
    try {
      await ensureConnected();
      const res = await client.auth.login(
        login.trim(),
        password,
        captchaHash,
        captchaText || 0,
        false,
      );
      if (res.status === 4) {
        const url = typeof res.captchaUrl === 'string' ? res.captchaUrl : '';
        setCaptchaUrl(url);
        setCaptchaText('');
        const match = url.match(/[?&]sig=([^&]+)/);
        setCaptchaHash(match?.[1] || 0);
        setError(res.message || 'Введите код с картинки');
        setPhase('form');
        return;
      }
      if (!res.success) {
        setError(res.message || res.error || `Ошибка входа (${res.status})`);
        setPhase('form');
        return;
      }
      const id = client.auth.userId;
      const hash = client.auth.loginKey;
      if (!id || !hash) {
        throw new Error('После входа нет member_id / pass_hash');
      }
      await finishWithSession(id, hash);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('form');
    }
  }

  async function onSessionSubmit(e: FormEvent) {
    e.preventDefault();
    const id = parseInt(memberId, 10);
    const hash = passHash.trim();
    if (!Number.isFinite(id) || id <= 0 || !hash) {
      setError('Нужны member_id и pass_hash');
      return;
    }
    setPhase('submitting');
    setError('');
    try {
      await ensureConnected();
      await client.auth.loginBySession(id, hash, false);
      await finishWithSession(id, hash);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('form');
    }
  }

  return (
    <div className="card">
      <h1>Подключение к tickets-bot</h1>
      <p className="lead">
        Войдите в аккаунт модератора 4PDA. Пароль уходит только на серверы 4PDA;
        боту сохраняется pass_hash.
      </p>

      <div className="tabs">
        <button
          type="button"
          className={mode === 'password' ? 'active' : ''}
          disabled={busy}
          onClick={() => setMode('password')}
        >
          Логин / пароль
        </button>
        <button
          type="button"
          className={mode === 'session' ? 'active' : ''}
          disabled={busy}
          onClick={() => setMode('session')}
        >
          Сессия
        </button>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {mode === 'password' ? (
        <form onSubmit={onPasswordSubmit}>
          <label htmlFor="login">Логин или e-mail</label>
          <input
            id="login"
            autoComplete="username"
            value={login}
            disabled={busy}
            onChange={(e) => setLogin(e.target.value)}
          />
          <label htmlFor="password">Пароль</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
          />
          {captchaUrl ? (
            <div className="captcha">
              <img src={captchaUrl} alt="Капча" />
              <label htmlFor="captcha">Код с картинки</label>
              <input
                id="captcha"
                value={captchaText}
                disabled={busy}
                onChange={(e) => setCaptchaText(e.target.value)}
              />
            </div>
          ) : null}
          <button type="submit" disabled={busy}>
            {busy ? 'Подключаем…' : 'Войти и подключить'}
          </button>
        </form>
      ) : (
        <form onSubmit={onSessionSubmit}>
          <label htmlFor="memberId">member_id</label>
          <input
            id="memberId"
            inputMode="numeric"
            value={memberId}
            disabled={busy}
            onChange={(e) => setMemberId(e.target.value)}
          />
          <label htmlFor="passHash">pass_hash</label>
          <input
            id="passHash"
            value={passHash}
            disabled={busy}
            onChange={(e) => setPassHash(e.target.value)}
          />
          <button type="submit" disabled={busy}>
            {busy ? 'Подключаем…' : 'Проверить и подключить'}
          </button>
        </form>
      )}

      <p className="footer">
        Ссылка одноразовая и с ограниченным сроком. Не пересылайте её третьим лицам.
      </p>
    </div>
  );
}
